import { Link } from "react-router-dom";
import { footerColumns } from "@/data/site";
import Logo from "@/components/feature/Logo";
import {useSiteSettingsContext} from "@/hooks/useSiteSettingsContext";

const socialDefs = [
  { key: "instagram", icon: "ri-instagram-line", label: "Instagram" },
  { key: "twitter", icon: "ri-twitter-x-line", label: "X (Twitter)" },
  { key: "youtube", icon: "ri-youtube-line", label: "YouTube" },
  { key: "linkedin", icon: "ri-linkedin-box-line", label: "LinkedIn" },
] as const;

export default function Footer() {
  const { settings } = useSiteSettingsContext();
  const name = settings.siteName || "DersDinle";
  const description =
    settings.siteDescription?.trim().replace(/dijital dershane/gi, 'dijital eğitim platformu') ||
    "Derslerin Sesi, Başarının Yolu.... Okul öncesinden üniversite ve kamu sınavlarına kadar tüm eğitim ihtiyaçların tek platformda.";

  const contacts = [
    settings.contactEmail
      ? { icon: "ri-mail-line", value: settings.contactEmail, href: `mailto:${settings.contactEmail}` }
      : null,
    settings.supportPhone
      ? { icon: "ri-phone-line", value: settings.supportPhone, href: `tel:${settings.supportPhone.replace(/\s/g, "")}` }
      : null,
    settings.whatsapp
      ? { icon: "ri-whatsapp-line", value: "WhatsApp Destek", href: `https://wa.me/${settings.whatsapp.replace(/\D/g, "")}` }
      : null,
  ].filter(Boolean) as { icon: string; value: string; href: string }[];

  const socials = socialDefs
    .map((s) => ({ ...s, href: String(settings[s.key] ?? "") }))
    .filter((s) => s.href.trim() !== "");

  return (
    <footer className="bg-primary-950 text-background-100 pb-safe-nav">
      <div className="h-px bg-gradient-to-r from-transparent via-accent-500/40 to-transparent"></div>

      <div className="px-4 md:px-6 lg:px-10 py-14 md:py-20">
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-10 lg:gap-8">
          <div className="col-span-2 md:col-span-3 lg:col-span-2">
            <Link to="/" className="flex items-center shrink-0" aria-label={`${name} ana sayfa`}>
              <Logo variant="dark" size="lg" />
            </Link>
            <p className="mt-5 text-sm leading-relaxed text-background-100/70 max-w-xs">{description}</p>

            {contacts.length > 0 && (
              <ul className="mt-5 space-y-2">
                {contacts.map((c) => (
                  <li key={c.href}>
                    <a
                      href={c.href}
                      className="inline-flex items-center gap-2 text-sm text-background-100/70 hover:text-accent-300 transition-colors"
                      rel="nofollow"
                    >
                      <i className={`${c.icon} text-base`} aria-hidden="true"></i>
                      {c.value}
                    </a>
                  </li>
                ))}
              </ul>
            )}

            {socials.length > 0 && (
              <div className="mt-5 flex items-center gap-2">
                {socials.map((s) => (
                  <a
                    key={s.key}
                    href={s.href}
                    target="_blank"
                    rel="noreferrer nofollow"
                    aria-label={s.label}
                    className="w-9 h-9 flex items-center justify-center rounded-md bg-white/10 text-background-100/80 hover:bg-accent-500 hover:text-background-50 transition-colors"
                  >
                    <i className={`${s.icon} text-base`} aria-hidden="true"></i>
                  </a>
                ))}
              </div>
            )}
          </div>

          {footerColumns.map((col) => (
            <div key={col.title}>
              <h4 className="font-heading font-semibold text-sm text-background-50 mb-5">
                {col.title}
              </h4>
              <ul className="space-y-3">
                {col.links.map((link) => (
                  <li key={`${link.label}-${link.href}`}>
                    <Link
                      to={link.href}
                      className="text-sm text-background-100/65 hover:text-accent-300 transition-colors"
                    >
                      {link.label}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>

        <div className="flex flex-col sm:flex-row items-center justify-between gap-4 mt-14 pt-8 border-t border-background-100/10">
          <p className="text-xs text-background-100/55">
            © 2026 {name}. Tüm hakları saklıdır.
          </p>
          <div className="flex flex-wrap items-center gap-x-5 gap-y-2">
            <Link to="/kvkk" className="text-xs text-background-100/55 hover:text-background-50 transition-colors">
              KVKK
            </Link>
            <Link
              to="/gizlilik-politikasi"
              className="text-xs text-background-100/55 hover:text-background-50 transition-colors"
            >
              Gizlilik Politikası
            </Link>
            <Link
              to="/cerez-politikasi"
              className="text-xs text-background-100/55 hover:text-background-50 transition-colors"
            >
              Çerez Politikası
            </Link>
          </div>
        </div>
      </div>
    </footer>
  );
}